import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useMutate } from 'restful-react'
import useInput, { Conditions } from '../../services/useInput'
import { unstable_batchedUpdates } from 'react-dom'
import { useTranslation } from 'react-i18next'
import i18n from 'i18next'
import { MuseLogo } from '@/components/MuseLogo'
import { TextInput } from '@/UIComponents2/TextInput'
import { Button } from '@/UIComponents2/Button'
import AlertIcon from '@/CustomIcons/AlertIcon'
import { motion } from 'framer-motion'
import { CircleIconButton } from '@/UIComponents2/CircleIconButton'
import LeftArrowIcon from '@/CustomIcons/LeftArrowIcon'
import MailIcon from '@/CustomIcons/MailIcon'
import RightArrowIcon from '@/CustomIcons/RightArrowIcon'
import UnauthorizedPageBase from './UnauthorizedPageBase'
import { LanguagesPopover } from '@/components/LanguagesPopover'
import LanguageIcon from '@/CustomIcons/LanguageIcon'

function PasswordResetPage() {
    const history = useHistory()
    const { t } = useTranslation()

    const { mutate: postResetPassword, loading: isLoading } = useMutate({
        verb: 'POST',
        path: `/v1/requests/password-reset`,
    })

    const [errorText, setErrorText] = useState<string | null>(null)
    const [showError, setShowError] = useState<boolean>(false)

    const resetPasswordHandler = async (formdata: any) => {
        try {
            await postResetPassword(formdata)
            history.push('/reset-password-result?email=' + formdata.email)
        } catch (e) {
            if (!(e as any)?.data) {
                unstable_batchedUpdates(() => {
                    setErrorText("Couldn't connect to the server. Please make sure your are connected to the internet.")
                    setShowError(true)
                })
            } else {
                const code = (e as any)?.data?.code
                if (code === 'not_found') {
                    unstable_batchedUpdates(() => {
                        setErrorText(`We couldn't find a user in our system with that email.`)
                        setShowError(true)
                    })
                } else {
                    unstable_batchedUpdates(() => {
                        setErrorText(
                            `Unknown error: status=${(e as any)?.status} message=${(e as any)?.message} code=${(e as any)?.data.code}`
                        )
                        setShowError(true)
                    })
                }
            }
        }
    }

    return (
        <UnauthorizedPageBase loading={isLoading}>
            <div className="flex justify-between items-center">
                <div className="w-[108px] h-[24px]">
                    <MuseLogo />
                </div>
                <LanguagesPopover>
                    <CircleIconButton>
                        <LanguageIcon />
                    </CircleIconButton>
                </LanguagesPopover>
            </div>

            <motion.div
                key="reset-form"
                className="flex flex-col gap-[48px] justify-center h-[80%]"
                initial={{ x: 50, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -50, opacity: 0 }}
                transition={{
                    duration: 0.3,
                    ease: 'easeOut',
                }}
            >
                <div className="-mt-16">
                    <CircleIconButton onClick={() => history.push('/login')} aria-label="Back to login">
                        <LeftArrowIcon style={{ fontSize: 28 }} />
                    </CircleIconButton>
                </div>
                <div className="flex flex-col gap-[14px]">
                    <span className="max-w-[442px] text-[30px]/[30px] font-[500] bg-linear-[98deg,white_21.88%,transparent_139.38%] bg-clip-text text-transparent">
                        {t('Reset your password')}
                    </span>
                    <span className="w-auto text-[16px] font-[400] text-[rgba(255,255,255,0.75)]">
                        {t('Enter your email to receive a password reset link')}
                    </span>
                </div>
                <FormContent
                    onSubmit={resetPasswordHandler}
                    isLoading={isLoading}
                    showError={showError}
                    errorText={errorText}
                    setShowError={setShowError}
                />
            </motion.div>
        </UnauthorizedPageBase>
    )
}

function FormContent({
    onSubmit,
    isLoading,
    showError,
    errorText,
    setShowError,
}: {
    onSubmit: (formdata: any) => void
    isLoading: boolean
    showError: boolean
    errorText: string | null
    setShowError: (showError: boolean) => void
}) {
    const { t } = useTranslation()
    const { formdata, bind, submit } = useInput(inputModel)

    const onSubmitHandler = async (e: React.FormEvent) => {
        e.preventDefault()
        setShowError(false)
        const { hasErrors } = await submit()
        if (!hasErrors) {
            onSubmit(formdata)
        }
    }

    return (
        <form
            noValidate
            onSubmit={onSubmitHandler}
            onKeyDown={(ev) => {
                if (ev.key === 'Enter') onSubmitHandler(ev)
            }}
            className="flex flex-col gap-[32px]"
        >
            {showError && (
                <div className={'text-black bg-[#FC5050] rounded-[20px] p-[16px] flex items-center gap-[8px]'}>
                    <AlertIcon className="w-[24px] h-[24px]" />
                    {errorText}
                </div>
            )}
            <TextInput
                placeholder={t('Email') || ''}
                {...bind.email}
                error={bind.email.error ? bind.email.helperText : undefined}
                helperText={undefined}
                className="bg-[rgba(255,255,255,0.08)]"
                icon={<MailIcon />}
                type="email"
            />
            <Button variant="primary" className="w-full relative" disabled={isLoading} onClick={onSubmitHandler}>
                {t('Reset Password')}
                <RightArrowIcon className="absolute right-4" />
            </Button>
        </form>
    )
}

const inputModel: any = {
    email: {
        errorChecks: [
            {
                trigger: ['onSubmit'],
                condition: Conditions.is_empty,
                message: () => i18n.t('This field is required'),
            },
        ],
    },
}

export default PasswordResetPage
